import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { ExternalBatchRiskAnalysisParametersFormFieldSchema } from '../batches/schemas';
import { ExternalDeclarationDocSchema, ExternalDeclarationLineDocSchema } from './external-declaration.docs';
import { ExternalAuditTrailDocSchema, ExternalTransitDocSchema, RISK_ANALYSIS_PARAMETERS_DESCRIPTION } from './external.docs';
import {
  ExternalAuthErrorResponses,
  ExternalBatchIdBodySchema,
  ExternalBatchNotFoundSchema,
  ExternalErrorSchema,
  ExternalForwarderGetBatchesResponseSchema,
  ExternalGetAuditTrailBodySchema,
  ExternalGetBatchesBodySchema,
  ExternalGetBatchesResponseSchema,
  ExternalGetBrokersResponseSchema,
  ExternalGetCustomersResponseSchema,
  ExternalGetDeclarationsBodySchema,
  ExternalGetTemplatesResponseSchema,
  ExternalGetTransitQuerySchema,
  ExternalGetUserResponseSchema,
  ExternalMessageResponseSchema,
  ExternalRouteMetadata,
  ExternalTag,
} from './external.schemas';

const c = initContract();

const lowValueSplit = (field: 'customer' | 'template', reason: string) =>
  z
    .string()
    .optional()
    .describe(
      `Low-value batches only. _id of the ${field} for consignments split out of the batch for ${reason}. ` +
        "If omitted, the declarant's default (manage account > default customers / templates) is used.",
    );

const routerOptions = {
  commonResponses: {
    500: ExternalErrorSchema.describe('Internal server error'),
  },
};

const declarantRoutes = c.router({
  authenticate: {
    method: 'POST',
    path: '/external/authenticate',
    summary: 'Authenticate user',
    description: 'Authenticate with email and password. Send the returned token as a bearer token on every other endpoint.',
    metadata: { tag: ExternalTag.Authentication, public: true } satisfies ExternalRouteMetadata,
    body: z.object({
      email: z.string().describe('email address of the user'),
      password: z.string(),
    }),
    responses: {
      200: z.object({ token: z.string() }).describe('User authenticated successfully'),
      401: ExternalErrorSchema.describe('Invalid credentials'),
      404: ExternalErrorSchema.describe('The user does not exist'),
    },
  },
  getUser: {
    method: 'GET',
    path: '/external/user',
    summary: 'Get the current user',
    description: 'Get the information of the authenticated user.',
    metadata: { tag: ExternalTag.RetrieveData } satisfies ExternalRouteMetadata,
    responses: {
      200: z.object({ data: ExternalGetUserResponseSchema }),
      ...ExternalAuthErrorResponses,
    },
  },
  getTemplates: {
    method: 'GET',
    path: '/external/templates',
    summary: 'List templates',
    description: 'List the templates associated with the declarant of this user.',
    metadata: { tag: ExternalTag.RetrieveData } satisfies ExternalRouteMetadata,
    responses: {
      200: z.object({ data: z.array(ExternalGetTemplatesResponseSchema) }),
      ...ExternalAuthErrorResponses,
    },
  },
  getCustomers: {
    method: 'GET',
    path: '/external/customers',
    summary: 'List customers',
    description: 'List the customers associated with the declarant of this user that the user has API access to.',
    metadata: { tag: ExternalTag.RetrieveData } satisfies ExternalRouteMetadata,
    responses: {
      200: z.object({ data: z.array(ExternalGetCustomersResponseSchema) }),
      ...ExternalAuthErrorResponses,
    },
  },
  getBatches: {
    method: 'POST',
    path: '/external/batches',
    summary: 'List batches',
    description: 'List the batches associated with the declarant of this user. All filters are optional; unknown filters are rejected.',
    metadata: { tag: ExternalTag.RetrieveData } satisfies ExternalRouteMetadata,
    body: ExternalGetBatchesBodySchema,
    responses: {
      200: z.object({ data: z.array(ExternalGetBatchesResponseSchema) }),
      400: ExternalErrorSchema.describe('Invalid filters'),
      ...ExternalAuthErrorResponses,
    },
  },
  sendBatch: {
    method: 'POST',
    path: '/external/send-batch',
    summary: 'Send a batch to customs',
    description: 'Send a batch to customs. Currently only low value batches are supported.',
    metadata: { tag: ExternalTag.HandleBatches } satisfies ExternalRouteMetadata,
    body: ExternalBatchIdBodySchema.extend({
      sendPost: z.boolean().optional().describe('send the batch using the post flow (default true); false uses the pre flow'),
    }),
    responses: {
      200: ExternalMessageResponseSchema,
      400: ExternalErrorSchema.describe('The batch is not in a valid state to be sent'),
      ...ExternalAuthErrorResponses,
      404: ExternalBatchNotFoundSchema,
    },
  },
  customsSync: {
    method: 'POST',
    path: '/external/customs-sync',
    summary: 'Sync customs data',
    description: 'Request a sync of the customs data for a batch.',
    metadata: { tag: ExternalTag.HandleBatches } satisfies ExternalRouteMetadata,
    body: ExternalBatchIdBodySchema,
    responses: {
      200: ExternalMessageResponseSchema,
      ...ExternalAuthErrorResponses,
      404: ExternalBatchNotFoundSchema,
    },
  },
  uploadBatch: {
    method: 'POST',
    path: '/external/upload-batch',
    summary: 'Upload a batch',
    description: 'Create a batch from an .xlsx file. The batch is processed asynchronously; poll the batches endpoint for its status.',
    metadata: { tag: ExternalTag.HandleBatches, fileField: 'the .xlsx file of the batch' } satisfies ExternalRouteMetadata,
    contentType: 'multipart/form-data',
    body: z.object({
      customer: z.string().describe('_id of the customer'),
      name: z.string().describe('name of the batch'),
      template: z.string().describe('_id of the template'),
      hvSplittingCustomer: lowValueSplit('customer', 'exceeding the high-value threshold'),
      hvSplittingTemplate: lowValueSplit('template', 'exceeding the high-value threshold'),
      h7ExcludedCustomer: lowValueSplit('customer', 'carrying an H7-excluded HS code'),
      h7ExcludedTemplate: lowValueSplit('template', 'carrying an H7-excluded HS code'),
    }),
    responses: {
      200: ExternalMessageResponseSchema.describe('The batch creation has started'),
      400: ExternalErrorSchema.describe('Invalid fields or file'),
      ...ExternalAuthErrorResponses,
    },
  },
  updateBatch: {
    method: 'POST',
    path: '/external/update-batch',
    summary: 'Update a batch',
    description: 'Replace the file of an existing batch. The batch is reprocessed asynchronously.',
    metadata: { tag: ExternalTag.HandleBatches, fileField: 'the new .xlsx file of the batch' } satisfies ExternalRouteMetadata,
    contentType: 'multipart/form-data',
    body: ExternalBatchIdBodySchema,
    responses: {
      200: z.object({ data: z.string() }),
      400: ExternalErrorSchema.describe('Invalid file'),
      ...ExternalAuthErrorResponses,
      404: ExternalBatchNotFoundSchema,
    },
  },
  getDeclarations: {
    method: 'POST',
    path: '/external/declarations',
    summary: 'List declarations',
    description: 'List declarations (only the consolidated data of each declaration). All filters are optional.',
    metadata: { tag: ExternalTag.RetrieveData, responseDoc: ExternalDeclarationDocSchema } satisfies ExternalRouteMetadata,
    body: ExternalGetDeclarationsBodySchema,
    responses: {
      200: z.object({ data: z.array(z.any()) }),
      ...ExternalAuthErrorResponses,
      403: ExternalErrorSchema.describe('The user is not an external API user, or no accessible declarations were found'),
    },
  },
  getDeclarationLines: {
    method: 'POST',
    path: '/external/declaration-lines',
    summary: 'List declaration lines',
    description: 'List the declaration lines of a declaration.',
    metadata: { tag: ExternalTag.RetrieveData, responseDoc: ExternalDeclarationLineDocSchema } satisfies ExternalRouteMetadata,
    body: z.object({
      LRN: z.string().describe('LRN number of the declaration'),
    }),
    responses: {
      200: z.object({ data: z.array(z.any()) }),
      ...ExternalAuthErrorResponses,
      403: ExternalErrorSchema.describe('The user is not an external API user, or no accessible declarations were found'),
    },
  },
  getBatchReport: {
    method: 'POST',
    path: '/external/batch-report',
    summary: 'Download batch report',
    description:
      'Download the customs report of a processed batch. All declarations must be in a final status: ' +
      'released, exported, not released, accepted, in control or invalidated.',
    metadata: { tag: ExternalTag.HandleBatches, fileResponse: ['application/pdf', 'application/zip'] } satisfies ExternalRouteMetadata,
    body: ExternalBatchIdBodySchema,
    responses: {
      200: z.any().describe('A single PDF report, or a ZIP archive when the batch has several reports'),
      400: ExternalErrorSchema.describe('The customs report is not available yet'),
      ...ExternalAuthErrorResponses,
      403: ExternalErrorSchema.describe('The user is not an external API user, or has no access to this batch'),
      404: ExternalBatchNotFoundSchema,
    },
  },
  getAuditTrail: {
    method: 'POST',
    path: '/external/audit-trail',
    summary: 'Customs audit trail',
    description:
      'Return the customs audit trail of low value declarations (one document per LRN). Provide exactly one of batch or LRN. ' +
      'Available only when the audit trail is enabled for your account, and only for batches of customers you have API access to.',
    metadata: { tag: ExternalTag.RetrieveData, responseDoc: ExternalAuditTrailDocSchema } satisfies ExternalRouteMetadata,
    body: ExternalGetAuditTrailBodySchema,
    responses: {
      200: z.object({ data: z.array(z.any()) }),
      ...ExternalAuthErrorResponses,
      403: ExternalErrorSchema.describe('The user is not an external API user, or the audit trail is not enabled for this account'),
    },
  },
  getTransit: {
    method: 'GET',
    path: '/external/transit',
    summary: 'List transits',
    description: 'List the transit records associated with the declarant of this user.',
    metadata: { tag: ExternalTag.RetrieveData, responseDoc: ExternalTransitDocSchema } satisfies ExternalRouteMetadata,
    query: ExternalGetTransitQuerySchema,
    responses: {
      200: z.object({ data: z.array(z.any()) }),
      ...ExternalAuthErrorResponses,
    },
  },
});

export const externalDeclarantContract = c.router(declarantRoutes, routerOptions);

export const externalForwarderContract = c.router(
  {
    ...declarantRoutes,
    getBrokerList: {
      method: 'GET',
      path: '/external/brokers',
      summary: 'List brokers',
      description: 'List the customs brokers associated with the declarant of this user.',
      metadata: { tag: ExternalTag.RetrieveData } satisfies ExternalRouteMetadata,
      responses: {
        200: z.object({ data: z.array(ExternalGetBrokersResponseSchema) }),
        ...ExternalAuthErrorResponses,
      },
    },
    getBatches: {
      ...declarantRoutes.getBatches,
      responses: {
        ...declarantRoutes.getBatches.responses,
        200: z.object({ data: z.array(ExternalForwarderGetBatchesResponseSchema) }),
      },
    },
    sendToBroker: {
      method: 'POST',
      path: '/external/send-to-broker',
      summary: 'Send a batch to its broker',
      description: 'Transfer the batch file and its declarations to the broker assigned to the batch (see the broker field of upload-batch).',
      metadata: { tag: ExternalTag.HandleBatches } satisfies ExternalRouteMetadata,
      body: ExternalBatchIdBodySchema,
      responses: {
        200: ExternalMessageResponseSchema.describe('The batch was sent to the broker'),
        400: ExternalErrorSchema.describe('The batch has no broker assigned'),
        ...ExternalAuthErrorResponses,
        403: ExternalErrorSchema.describe('The user is not an external API user, or has no access to this batch'),
        404: ExternalBatchNotFoundSchema,
      },
    },
    uploadBatch: {
      ...declarantRoutes.uploadBatch,
      body: declarantRoutes.uploadBatch.body.extend({
        broker: z.string().optional().describe('_id of the broker the batch is created for (see brokers). The broker must belong to your declarant'),
        // customs trade only: JSON-encoded { evaluationMethods?, vatValidationEnabled?, eoriValidationEnabled?, agentPrompt? }
        riskAnalysisParameters: ExternalBatchRiskAnalysisParametersFormFieldSchema.optional().describe(RISK_ANALYSIS_PARAMETERS_DESCRIPTION),
      }),
      responses: {
        ...declarantRoutes.uploadBatch.responses,
        404: ExternalErrorSchema.describe('Broker not found'),
      },
    },
  },
  routerOptions,
);
